import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { GET_COMPANY_SETTINGS, UPDATE_COMPANY_SETTINGS } from '../../api';
import CustomFieldsSettings from './CustomFieldsSettings';

const DEFAULTS = {
  attendance_geofence_enforced: false,
  biometric_sync_enabled: true,
  lop_formula_type: 'calendar_days',
  allow_mobile_punch: true,
  pf_enabled: true,
};

const BOOLEAN_SETTINGS = [
  {
    key: 'attendance_geofence_enforced',
    label: 'Enforce branch geofence',
    help: 'Require attendance punches to be within the configured branch radius.',
  },
  {
    key: 'biometric_sync_enabled',
    label: 'Enable biometric synchronization',
    help: 'Allow authenticated biometric devices to submit attendance punches.',
  },
  {
    key: 'allow_mobile_punch',
    label: 'Allow mobile attendance punch',
    help: 'Allow employees to use the mobile punch flow when it is available.',
  },
  {
    key: 'pf_enabled',
    label: 'Enable PF deduction',
    help: 'Apply employee and employer PF during salary preview, processing, reports and payslips for this company.',
  },
];

const errorMessage = (error, fallback) => (
  error?.response?.data?.detail
  || error?.response?.data?.error
  || fallback
);

export default function CompanySettingsTab() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    GET_COMPANY_SETTINGS()
      .then((data) => {
        if (!active) return;
        setCompanyCode(data?.company_code || '');
        setSettings({ ...DEFAULTS, ...(data?.settings_json || {}) });
      })
      .catch((error) => {
        if (active) setMessage({ severity: 'error', text: errorMessage(error, 'Unable to load company settings.') });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const toggle = (key) => (event) => {
    setSettings((current) => ({ ...current, [key]: event.target.checked }));
    setMessage(null);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const data = await UPDATE_COMPANY_SETTINGS(settings);
      setSettings({ ...DEFAULTS, ...(data?.settings_json || {}) });
      setMessage({ severity: 'success', text: 'Company settings saved.' });
    } catch (error) {
      setMessage({ severity: 'error', text: errorMessage(error, 'Unable to save company settings.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>Company Settings</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Runtime attendance and payroll controls{companyCode ? ` for ${companyCode}` : ''}.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress aria-label="Loading company settings" /></Stack>
        ) : (
          <Stack spacing={3}>
            {BOOLEAN_SETTINGS.map((item) => (
              <Box key={item.key}>
                <FormControlLabel
                  control={<Switch checked={Boolean(settings[item.key])} onChange={toggle(item.key)} />}
                  label={item.label}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                  {item.help}
                </Typography>
              </Box>
            ))}

            <FormControl fullWidth>
              <InputLabel id="lop-formula-label">LOP calculation formula</InputLabel>
              <Select
                labelId="lop-formula-label"
                id="lop-formula"
                label="LOP calculation formula"
                value={settings.lop_formula_type}
                onChange={(event) => setSettings((current) => ({
                  ...current,
                  lop_formula_type: event.target.value,
                }))}
              >
                <MenuItem value="calendar_days">Actual calendar days</MenuItem>
                <MenuItem value="fixed_30_days">Fixed 30 days</MenuItem>
                <MenuItem value="working_days">Working days</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                onClick={save}
              >
                {saving ? 'Saving...' : 'Save settings'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
      <CustomFieldsSettings />
    </Box>
  );
}
